import type { ColumnType } from 'typeorm'
import type { Parameter } from './script/parameter.js'
import type {
	ClientLogLevel,
	FindManyOptions,
	PagedData,
	PagingDefinition,
	ReportResult,
	ValidationError,
} from './app-types.js'
import { Lock } from './lock/lock.js'
import * as z from 'zod'
import { UserIdType } from './user-id.js'
import { BoReference } from './bos/bo-reference.js'
import { Observable } from 'rxjs'
import { EditorPropertyWithKey } from './blocks/decorators/editor-property-types.js'
import type { Signal, WritableSignal } from '@angular/core'
import { ReportView, ReportViewAggregation } from './bos/report-view.js'
import { ReportViewStatic } from './bos/report-view-static.js'
import type {
	ScreenSize,
	ScreenSizes,
	BoInfoSymbol,
	HasBoInfo,
	HasChangedInternallySymbol,
} from './consts.js'
/**
 * @deprecated
 */
export type Newable<T, A extends any[] = any[]> = { new (...args: A): T }

export const BusinessObjectTypes = [
	'Module',
	'Screen',
	'Entity',
	'StaticEntity',
	'Process',
	'ServerDataStore',
	'Label',
	'AccessControl',
	'ClientDataStore',
	'ConfigurationItem',
	'Role',
	'Permission',
	'DataType',
	'Report',
	'ServiceIntegration',
	'ServiceProvider',
	'Resource',
	'Language',
	'Trigger',
	'Theme',
	'UserDefinedConfiguration',
] as const
export const BusinessObjectInfos: Record<
	BusinessObjectTypeType,
	{
		pluralName: string
		hasEditor: boolean
	}
> = {
	Module: { pluralName: 'Modules', hasEditor: true },
	ClientDataStore: { pluralName: 'ClientDataStores', hasEditor: true },
	Entity: { pluralName: 'Entities', hasEditor: true },
	StaticEntity: { pluralName: 'StaticEntities', hasEditor: true },
	Screen: { pluralName: 'Screens', hasEditor: true },
	Process: { pluralName: 'Processes', hasEditor: true },
	ConfigurationItem: { pluralName: 'ConfigurationItems', hasEditor: true },
	Label: { pluralName: 'Labels', hasEditor: true },
	Role: { pluralName: 'Roles', hasEditor: true },
	Permission: { pluralName: 'Permissions', hasEditor: true },
	DataType: { pluralName: 'DataTypes', hasEditor: true },
	Report: { pluralName: 'Reports', hasEditor: true },
	ServerDataStore: { pluralName: 'ServerDataStores', hasEditor: true },
	ServiceIntegration: { pluralName: 'ServiceIntegrations', hasEditor: true },
	ServiceProvider: { pluralName: 'ServiceProviders', hasEditor: true },
	Resource: { pluralName: 'Resources', hasEditor: true },
	Language: { pluralName: 'Languages', hasEditor: false },
	AccessControl: { pluralName: 'AccessControls', hasEditor: true },
	Trigger: { pluralName: 'Triggers', hasEditor: true },
	Theme: { pluralName: 'Themes', hasEditor: true },
	UserDefinedConfiguration: {
		pluralName: 'UserDefinedConfigurations',
		hasEditor: true,
	},
}

export interface BusinessObjectItem {
	__type?: string
	type?: string
	getAdditionalEditorProperties?(): EditorPropertyWithKey[]
	businessObject?: any
}

export type BoParamsType = {
	branchName: BranchNameType
	moduleId: ModuleIdType
	boType: BusinessObjectTypeType
	boId: BusinessObjectIdType
}

export type BusinessObjectTypeType = (typeof BusinessObjectTypes)[number]
export type BusinessObjectTypesOrAll = BusinessObjectTypeType[] | '*'
export type BusinessObjectIdType = string
export type ModuleIdType = string
export type ModuleBundleType = string
export type BranchNameType = string
export * from './user-id.js'

export type HttpMethod =
	| 'GET'
	| 'POST'
	| 'PUT'
	| 'DELETE'
	| 'PATCH'
	| 'HEAD'
	| 'JSONP'
	| 'OPTIONS'

export const MAXLENS = {
	moduleId: 50,
	moduleBundle: 100,
	moduleName: 256,
	boId: 100,
	boType: 25,
	boQualifiedName: -1,
	branchName: 50,
	propertyName: 100,
	userName: 50,
	userFirstLastName: 50,
	userFullName: -1,
	email: 100,
	phoneNumber: 100,
	password: 100,
	passwordHash: 60,
	jsonConfig: 4000,
	filePath: 2048,
	lockObjectType: 30,
	lockObjectName: 150,
	lockLevel: 20,
	threadName: 30,
	logType: 10,
	logComponent: 30,
	extensionId: 50,
	mimeType: 100,
	genericName: 50,
	certificatePem: undefined,
	publicKeyPem: 1000,
	privateKeyPem: 4000,
	ipV4Address: 15,
	ipV6Address: 39,
	hostname: 250,
	groupName: 100,
	processActivityId: 50,
	processExecutionId: 50,
	processTaskName: 200,
	userAuthentication: 50,
	userAuthenticationToken: 20,
	tenantId: 50,
	tenantName: 100,
	error: undefined,
	sessionId: 36,
	uuid: 36,
	aiFirstHumanMessage: 100,
	authProviderClientSecret: 40,
	authProviderUrl: 200,
	authAccessToken: undefined,
	authRefreshToken: 2500,
	authScope: 200,
	comment: undefined,
	objectId: 50,
	auditActionType: 50,
}

MAXLENS.boQualifiedName = MAXLENS.moduleId + MAXLENS.boId + 1
MAXLENS.userFullName = 2 * MAXLENS.userFirstLastName + 1

export type CodeEditorSize = 'normal' | 'singleline' | 'large' | 'fill'

export type TsCodeType = string
export type JsCodeType = string
export type JsonType = string
export type VariableNameType = string
export type TsTypeType = string
export type HtmlType = string
export type ScssType = string
export type RoutePathType = string

export const PrimitiveTypeNames = [
	'',
	'void',
	'string',
	'number',
	'boolean',
	'Date',
] as const
export type PrimitiveType = (typeof PrimitiveTypeNames)[number]

export interface DeclarationsContainer {
	declarations: string[]
	doEncloseInNamespace: boolean
	imports: Record<string, string> // identifier (can be '* as Alias') -> moduleName
}

export type NonFunctionPropertyNames<T> = {
	[K in keyof T]: T[K] extends Function ? never : K
}[keyof T]
export type DeepReadonly<T> = T extends any[]
	? DeepReadonlyArray<T[number]>
	: T extends object
		? DeepReadonlyObject<T>
		: T

export interface DeepReadonlyArray<T> extends ReadonlyArray<DeepReadonly<T>> {}

export type DeepReadonlyObject<T> = {
	// readonly [P in NonFunctionPropertyNames<T>]: T[P] extends Date ? Date : DeepReadonly<T[P]>;
	readonly [P in keyof T]: T[P] extends Function ? T[P] : DeepReadonly<T[P]>
}

export type NominalType<RealType, Marker> = RealType & { __marker?: Marker }
export interface ScreenDialogOptions {
	disableClose?: boolean
	hasBackdrop?: boolean
	height?: string
	width?: string
	maxHeight?: string | number
	minHeight?: string | number
	maxWidth?: string | number
	minWidth?: string | number
	panelClass?: string | string[]
	position?: { top?: string; bottom?: string; left?: string; right?: string }
}
export interface ScreenDefinition<
	P,
	I,
	O extends Record<string, any> = Record<string, any>,
> {
	moduleId: string
	boId: string
	component: Class
	getFullUrl(params: P): string
	openAsDialog<X extends keyof O>(
		inputData: P & I,
		outputName: X,
		options?: ScreenDialogOptions,
	): Promise<O[X]>
	__pathParams?: P
	__Inputs?: I
	__Outputs?: O
}
export interface HasId {
	id: string | number
}
export type IdType<T extends HasId = HasId> = T['id']
export interface HasType {
	readonly __type: string
}
export interface HasBaseType {
	readonly __baseType: string
}
export interface HasBoType {
	readonly __boType: string
}

// export interface EntityClass extends HasId, HasType {
// }

// See DataSourceOption['type']
export const DbTypeNames = {
	postgres: 'PostgreSQL',
	mssql: 'MS SQL',
	mariadb: 'MariaDB',
	mysql: 'MySql',
	sqlite: 'SqLite',
	oracle: 'Oracle',
} as const
export type DbTechnology = keyof typeof DbTypeNames

export const DbTypeConfigSources = {
	default: 'Default for TypeScript type',
	custom: 'Customize',
	manual: 'Manually define SQL type',
} as const
export type DbTypeConfigSource = keyof typeof DbTypeConfigSources

export const DbStandardTypeList = [
	'numericPrimaryKey',
	'number',
	'string',
	'Date',
	'boolean',
	'blob',
	'clob',
	'char',
	'ArrayBuffer',
	'Buffer',
	'double',
	'json',
	'uuid',
	'vector',
] as const
export type DbStandardTypes = {
	[K in (typeof DbStandardTypeList)[number]]: K extends 'numericPrimaryKey'
		? ColumnType | 'serial'
		: ColumnType
}
export const DbTypes: Record<DbTechnology, DbStandardTypes> = {
	postgres: {
		numericPrimaryKey: 'serial',
		number: 'int',
		string: 'varchar',
		Date: 'timestamp',
		boolean: 'boolean',
		blob: 'bytea',
		clob: 'text',
		char: 'char',
		ArrayBuffer: 'bytea',
		Buffer: 'bytea',
		double: 'decimal',
		json: 'json',
		uuid: 'uuid',
		vector: 'vector',
	},
	sqlite: {
		numericPrimaryKey: 'int',
		number: 'int',
		string: 'varchar',
		Date: 'datetime',
		boolean: 'boolean',
		blob: 'blob',
		clob: 'text',
		char: 'character',
		ArrayBuffer: 'bytea',
		Buffer: 'bytea',
		double: 'double',
		json: 'simple-json',
		uuid: 'uuid',
		vector: 'vector',
	},
	mssql: {
		numericPrimaryKey: 'int',
		number: 'int',
		string: 'nvarchar',
		Date: 'datetime2',
		boolean: 'bit',
		blob: 'varbinary',
		clob: 'nvarchar',
		char: 'char',
		ArrayBuffer: 'varbinary',
		Buffer: 'varbinary',
		double: 'float',
		json: 'nvarchar',
		uuid: 'uuid',
		vector: 'vector',
	},
	mariadb: {
		numericPrimaryKey: 'int',
		number: 'int',
		string: 'varchar',
		Date: 'datetime',
		boolean: 'boolean',
		blob: 'bytea',
		clob: 'clob',
		char: 'char',
		ArrayBuffer: 'bytea',
		Buffer: 'bytea',
		double: 'double',
		json: 'json',
		uuid: 'uuid',
		vector: 'vector',
	},
	mysql: {
		numericPrimaryKey: 'int',
		number: 'int',
		string: 'varchar',
		Date: 'datetime',
		boolean: 'boolean',
		blob: 'bytea',
		clob: 'clob',
		char: 'char',
		ArrayBuffer: 'bytea',
		Buffer: 'bytea',
		double: 'double',
		json: 'json',
		uuid: 'uuid',
		vector: 'vector',
	},
	oracle: {
		numericPrimaryKey: 'int',
		number: 'int',
		string: 'varchar',
		Date: 'datetime',
		boolean: 'boolean',
		blob: 'bytea',
		clob: 'clob',
		char: 'char',
		ArrayBuffer: 'bytea',
		Buffer: 'bytea',
		double: 'double',
		json: 'simple-json',
		uuid: 'uuid',
		vector: 'vector',
	},
} as const

export const DbTechnologyCapabilities: Record<
	DbTechnology,
	{
		enum: boolean
		json: boolean
	}
> = {
	postgres: {
		enum: true,
		json: true,
	},
	sqlite: {
		enum: true,
		json: true,
	},
	mssql: {
		enum: false,
		json: false,
	},
	mariadb: {
		enum: true,
		json: true,
	},
	mysql: {
		enum: true,
		json: true,
	},
	oracle: {
		enum: false,
		json: false,
	},
}

export function dbMaxLength(dbTechnology: DbTechnology, columnType: string) {
	if (dbTechnology == 'mssql') {
		if (columnType == 'string') return 'MAX'
		if (columnType == 'clob') return 'MAX'
	}
	return undefined
}

type DbVectorOperatorFn = (columnName: string, vectorStr: string) => string
export const DbVectorOperators = {
	l1Distance: (columnName, vectorStr) => `${columnName} <+> ${vectorStr}`,
	l2Distance: (columnName, vectorStr) => `${columnName} <-> ${vectorStr}`,
	innerProduct: (columnName, vectorStr) =>
		`(${columnName} <#> ${vectorStr}) * -1`,
	cosineDistance: (columnName, vectorStr) =>
		`1 - (${columnName} <=> ${vectorStr})`,
} as const satisfies Record<string, DbVectorOperatorFn>
export type DbVectorOperator = keyof typeof DbVectorOperators

export const BoReferencesFilters = [
	'cssResources',
	'dbConnectionConfigItem',
	'mailConnectionConfigItem',
] as const
export type BoReferencesFilter = (typeof BoReferencesFilters)[number]

export type WrapperType = [string, string]

export interface ExecutionResult {
	responses: WritableSignal<ExecutionControllerExecutionResponse[]>
	timeMs: WritableSignal<number | undefined>
}
export type ExecutionControllerExecutionResponse = {
	data?: any
	error?: any
	stackTrace?: string[]
	logMessages?: [ClientLogLevel, any[]][]
}

export type WatcherEventName =
	| 'add'
	| 'addDir'
	| 'change'
	| 'unlink'
	| 'unlinkDir'

export interface MainScreenClass {
	$canBeMainScreen: true
}
export interface DialogScreenClass {
	$canBeDialogScreen: true
}
export interface NameAndType {
	name: string
	type: string
	isArray?: boolean
}

export interface BoTypeInformation {
	boType: BusinessObjectTypeType
	properties: NameAndType[]
}

export interface EventType {
	name: string
	prefix?: string
	parameters?: Parameter[]
}

export type RecordOrFunction<K extends string | number | symbol, V> =
	| Record<K, V>
	| ((input: K) => V)

export const DirectionOptions = {
	vertical: 'vertical',
	horizontal: 'horizontal',
} as const
export const GridLayoutDirectionOptions = {
	...DirectionOptions,
	inherit: 'inherit',
} as const

export const FlexLayoutDirectionOptions = {
	vertical: 'vertical in one column',
	horizontal: 'horizontal in one row',
	'grid-horizontal': 'grid with fixed columns',
	'grid-vertical': 'grid with fixed rows',
	block: 'normal <div> block',
	// columns: 'fixed number of columns',
	'vertical-reverse': 'vertical (bottom-up)',
	'horizontal-reverse': 'horizontal (right-to-left)',
	inherit: 'inherit',
} as const

export type Direction = keyof typeof DirectionOptions
export type GridLayoutDirection = keyof typeof GridLayoutDirectionOptions
export type FlexLayoutDirection = keyof typeof FlexLayoutDirectionOptions

export const LayoutAlignmentOptions = {
	inherit: 'inherit (default)',
	start: 'start',
	center: 'center',
	end: 'end',
	stretch: 'stretch',
	baseline: 'baseline',
} as const
export type LayoutAlignmentOption = keyof typeof LayoutAlignmentOptions

export type IfEquals<X, Y, A = X, B = never> =
	(<T>() => T extends X ? 1 : 2) extends <T>() => T extends Y ? 1 : 2 ? A : B

export type WritableKeys<T> = {
	[P in keyof T]-?: IfEquals<
		{ [Q in P]: T[P] },
		{ -readonly [Q in P]: T[P] },
		P
	>
}[keyof T]

export type OnlyData<T> = {
	[P in keyof T as T[P] extends Class<any>
		? P
		: T[P] extends Function
			? never
			: P extends '__type'
				? never
				: P]: T[P]
}

export type OnlyWritableData<T> = {
	[P in WritableKeys<OnlyData<T>>]: T[P]
}

class X {
	readonly foo = 7
	bar = 8
}

export type PartialData<T> = Partial<OnlyData<T>>

export type DeepRequired<T> = {
	[P in keyof T]-?: T[P] extends object ? DeepRequired<T[P]> : T[P]
}
export type UnwrapReadonly<T> = T extends readonly (infer U)[]
	? U[]
	: T extends ReadonlyArray<infer U>
		? U[]
		: T extends Readonly<infer U>
			? U
			: T

export interface DbQueryStructure {
	sql: string
	parameters: any[]
}

export type Class<T = any, A extends any[] = any[]> = new (...args: A) => T
export type AbstractClass<T = any, A extends any[] = any[]> = abstract new (
	...args: A
) => T
export type ModuleCode = Record<string, Class>

export type Interface<
	C extends Class<InstanceType<C>> | AbstractClass<InstanceType<C>>,
	SI,
	I = {},
> =
	C extends Class<InstanceType<C>>
		? // ConcreteClass
			InstanceType<C> extends I
			? C extends SI & Class<InstanceType<C>>
				? InstanceType<C> & I
				: SI & Class<InstanceType<C>> // Indicate StaticInterface Error
			: I // Indicate Interface Error
		: // AbstractClass
			InstanceType<C> extends I
			? C extends SI & AbstractClass<InstanceType<C>>
				? InstanceType<C> & I
				: SI & AbstractClass<InstanceType<C>> // Indicate StaticInterface Error
			: I // Indicate Interface Error

export interface HasInternalChangeObservable {
	readonly [HasChangedInternallySymbol]?: Observable<void>
}
export const NotReadySymbol = Symbol('NotReady')

interface CommonBoInfo {
	boType: BusinessObjectTypeType
}
interface EntityAndStaticEntityInfo extends CommonBoInfo {
	readonly editablePropertyNames: readonly string[]
	readonly computedPropertyNames: readonly string[]
	readonly allPropertyNames: readonly string[]
	readonly propertyNamesOfInterest: readonly string[]
}
export interface EntityInfo extends EntityAndStaticEntityInfo {
	readonly boType: 'Entity'
	readonly idType: string
	readonly propertyTypes: Readonly<Record<string, string>>
	readonly staticEntityProperties: readonly string[]
	readonly searchInProperties: readonly string[]
	readonly subsetNames: readonly string[]
	readonly manyToOneRelationships: Readonly<Record<string, () => EntityClass>>
	readonly oneToManyRelationships: Readonly<Record<string, () => EntityClass>>
	readonly oneToOneOwningRelationships: Readonly<
		Record<string, () => EntityClass>
	>
	readonly oneToOneSecondaryRelationships: Readonly<
		Record<string, () => EntityClass>
	>
	readonly allRelationships: Readonly<Record<string, () => EntityClass>>
	readonly embeddedEntities: Readonly<Record<string, () => EntityClass>>
}
export interface StaticEntityInfo extends EntityAndStaticEntityInfo {}
export interface ServerDataStoreInfo {}
export interface ReportInfo extends CommonBoInfo {
	staticViews: ReportViewStatic[]
	fullDataProperties: string[]
}

export type BoInfo<T extends BusinessObjectTypeType = BusinessObjectTypeType> =
	(EntityInfo | StaticEntityInfo | ServerDataStoreInfo | ReportInfo) & {
		boType: T
	}

export interface CallableBo<
	T extends BusinessObjectTypeType = BusinessObjectTypeType,
> extends HasBoInfo<T> {
	readonly __type?: string
}

export interface OnAfterCodeLoad extends Class {
	θafterCodeLoad(): Promise<void>
}

export interface EntityCommon extends CallableBo<'Entity'> {
	readonly __baseType: string
	readonly [BoInfoSymbol]: EntityInfo
}
export interface EntityClass<T = any> extends EntityCommon {
	new (init?: Partial<T>): T
}
export interface EntityClassOnDb<T = any> extends EntityClass<T & HasId> {}
export interface EntityInstance extends EntityCommon {}

export interface EntityInstanceOnDb extends EntityInstance {
	id: string | number
}

export type EntityRecord<T extends EntityCommon> = {
	[P in T[typeof BoInfoSymbol]['allPropertyNames'][number]]: P extends keyof T
		? T[P]
		: any
}

export interface ReportClass extends CallableBo<'Report'> {
	readonly baseEntity: EntityClassOnDb
	[functionName: string]: ((options: ReportView) => Promise<ReportResult>) | any
}

export type SavedIds<T extends EntityInstanceOnDb> = {
	[P in
		| 'id'
		| keyof T[typeof BoInfoSymbol]['oneToManyRelationships']
		| keyof T[typeof BoInfoSymbol]['manyToOneRelationships']]: P extends 'id'
		? T['id']
		: P extends keyof T[typeof BoInfoSymbol]['oneToManyRelationships']
			? (number | string)[]
			: number | string
}

export interface ServerDataStoreClass extends CallableBo<'ServerDataStore'> {
	// TODO: reactivate typed methods; this was deactivates as the PROD Angular compiler complained
	// loadById<T extends EntityInstanceOnDb>(type: Class<T>, id: T['id']): Promise<T>
	// loadByCondition<T extends EntityInstanceOnDb>(type: Class<T>, findOptions?: FindManyOptions<T>): Promise<T[]>
	// loadByQuery<T extends EntityInstanceOnDb>(type: Class<T>, query: DataQuery<T>, findOptions?: FindManyOptions<T>): Promise<T[]>
	// loadPageByQuery<T extends EntityInstanceOnDb>(type: Class<T>, paging: PagingDefinition, query: DataQuery<T>, findOptions?: Omit<FindManyOptions<T>, 'take' | 'skip'>): Promise<PagedData<T>>
	loadById<T = any>(type: Class<any>, id: any): Promise<any>
	loadByCondition<T = any>(
		type: Class<any>,
		findOptions?: FindManyOptions<any>,
	): Promise<any[]>
	loadByQuery<T = any>(
		type: Class<any>,
		query: DataQuery<any>,
		findOptions?: FindManyOptions<any>,
	): Promise<any[]>
	loadPageByQuery<T = any>(
		type: Class<any>,
		paging: PagingDefinition,
		query: DataQuery<any>,
		findOptions?: Omit<FindManyOptions<any>, 'take' | 'skip'>,
	): Promise<PagedData<any>>
}

export interface StaticEntityCommon extends CallableBo<'StaticEntity'> {}
export interface StaticEntityClass extends StaticEntityCommon {}
export interface StaticEntityInstance extends StaticEntityCommon {}

export interface DataQuery<T extends EntityInstance> {
	query: string
	searchInProperties?: T[typeof BoInfoSymbol]['allPropertyNames'][number][]
	caseSensitive?: boolean
}

export interface UserDefinedConfigurationClass<
	T = any,
> extends CallableBo<'UserDefinedConfiguration'> {
	get(): Promise<T>
	set(value: T): Promise<void>
}

export interface LanguageInfo {
	shortName: string
	displayName: string
	isoCode: string
}

export type SyncValidationRule<T = any> = (
	value: T,
	$scope: any,
) => ValidationError | undefined
export type AsyncValidationRule<T = any> = (
	value: T,
	$scope: any,
) => Promise<ValidationError | undefined>
export type ValidationRules<T = any> = {
	sync: SyncValidationRule<T>[]
	async: AsyncValidationRule<T>[]
}
/** @deprecated */
export type ThreadType =
	| 'main'
	| 'frontendCompiler'
	| 'backendCompiler'
	| 'requestWorker'
	| 'event'
	| 'static'
export const ServiceTypes = [
	'core',
	'event',
	'frontend-compiler',
	'backend-compiler',
	'app-api',
	'app-api-executor',
	'static',
] as const
export const ThreadRoles = ['standalone', ...ServiceTypes, 'init'] as const
export type ServiceType = (typeof ServiceTypes)[number]
export type ClusterMode = 'standalone' | 'cluster'
export type ThreadRole = (typeof ThreadRoles)[number]

export type BreakpointSection = Record<ScreenSize, boolean>

export interface Breakpoints {
	sizeEquals: BreakpointSection
	sizeIsGreaterThan: BreakpointSection
	sizeIsGreaterThanOrEqual: BreakpointSection
	sizeIsLessThan: BreakpointSection
	sizeIsLessThanOrEqual: BreakpointSection
	isHandset: boolean
	isTablet: boolean
	isWeb: boolean
	isHandsetPortrait: boolean
	isTabletPortrait: boolean
	isWebPortrait: boolean
	isHandsetLandscape: boolean
	isTabletLandscape: boolean
	isWebLandscape: boolean
	isPortrait: boolean
	isLandscape: boolean
	screenSize: (typeof ScreenSizes)[number]
	decideBySize<T>(options: Partial<Record<ScreenSize, T>> | [T, T, T, T, T]): T
}

export const ipcPort = 1500

export type CrudOperation = 'read' | 'create' | 'update' | 'delete'
export type ClientOrServer = 'client' | 'server'

export interface MenuEntry {
	label: string
	onClick?: () => void
	children?: MenuEntry[]
}

export interface TableViewConfiguration<T = any> {
	columns?: (keyof T & string)[]
	sortColumn?: keyof T & string
	sortDirection?: 'asc' | 'desc'
	filter?: string
}

export interface TableViewConfigurations<T = any> {
	defaultView: string
	views: Record<string, TableViewConfiguration<T>>
}

export const LogLevels = [
	'critical',
	'error',
	'warning',
	'info',
	'debug',
	'status',
] as const
export type LogLevel = (typeof LogLevels)[number]

export interface LockRequestResponse {
	successful: boolean
	message?: string
	lock?: Lock
}

type Key = string | number | symbol
type NestedRecord<K, V> = K extends Key ? Record<K, V> : never
export type Nested<
	K1 extends Key,
	K2,
	K3 = never,
	K4 = never,
	K5 = never,
	K6 = never,
> = [K3] extends [never]
	? Record<K1, K2>
	: [K4] extends [never]
		? Record<K1, NestedRecord<K2, K3>>
		: [K5] extends [never]
			? Record<K1, NestedRecord<K2, NestedRecord<K3, K4>>>
			: [K6] extends [never]
				? Record<K1, NestedRecord<K2, NestedRecord<K3, NestedRecord<K4, K5>>>>
				: Record<
						K1,
						NestedRecord<
							K2,
							NestedRecord<K3, NestedRecord<K4, NestedRecord<K5, K6>>>
						>
					>

export const LaneAssigneeTypes = {
	group: 'Group',
	role: 'Role',
	user: 'Specific user (not encouraged)',
} as const
export type LaneAssigneeType = keyof typeof LaneAssigneeTypes

export interface HasTokenAssignment {
	readonly allowedUserIds: UserIdType[]
	readonly allowedGroups: string[]
	readonly allowedRoles: string[]
}

export const PagingSchema = z.strictObject({
	pageSize: z.number(),
	pageIndex: z.number(),
})
export const SortingSchema = z.strictObject({
	by: z.string(),
	direction: z.enum(['asc', 'desc']).optional(),
})
export const AllTokenFilterSchema = z.strictObject({
	moduleIds: z.union([z.string().array(), z.literal('*')]).optional(),
})
export type AllTokenFilter = z.infer<typeof AllTokenFilterSchema>
export const UserRelatedTokenFilterSchema = AllTokenFilterSchema.extend({
	userId: z.string().optional(),
	includeAssigned: z.boolean().optional(),
	includeUserAllowed: z.boolean().optional(),
	includeRoleAllowed: z.boolean().optional(),
	includeGroupAllowed: z.boolean().optional(),
	moduleIds: z.union([z.string().array(), z.literal('*')]).optional(),
	// includePreviouslyClaimed: z.boolean(),
})
export type UserRelatedTokenFilter = z.infer<
	typeof UserRelatedTokenFilterSchema
>

export type LoginMethod = 'credentials' | 'sso'

export interface ProcessActivityInfo {
	screenRef: string
	outputs: string[]
	outgoingFlows: ProcessOutgoingFlowInfo[]
	swimlaneName: string
}

export interface ProcessOutgoingFlowInfo {
	flowId: string
	flowName: string
	nextUserTaskName: string
	isDefault: boolean
}

export type MaybeAsync<T> = T | Promise<T> | Observable<T>

type JoinedKeyAndPathOfValue<
	T extends object,
	FindType,
	AllowArrays extends boolean,
	StopTypes,
> = {
	[K in keyof T]: K extends string
		? T[K] extends StopTypes
			? never
			: `${K}${JoinedPathForType<T[K], FindType, AllowArrays, StopTypes>}`
		: never
}
export type JoinedPathForType<
	T,
	FindType,
	AllowArrays extends boolean = true,
	StopTypes = never,
	AccumPath extends string = '',
> = T extends FindType
	? AccumPath
	: AllowArrays extends true
		? T extends Array<infer U>
			? `${AccumPath}/*${JoinedPathForType<U, FindType, AllowArrays, StopTypes>}`
			: never
		: T extends object
			? `${AccumPath}/${JoinedKeyAndPathOfValue<T, FindType, AllowArrays, StopTypes>[keyof T]}`
			: never

// Re-export missing types and utilities
export type { JsonMappable } from './data/json-mappable.js'
export type { CanBeSecret } from './data/can-be-secret.js'
export { createSetNotifyProxy } from './util/create-set-notify-proxy.js'
export { LeafBlock } from './blocks/leaf-block.js'
export type { NumberPropertyKeys, TypedPropertyKeys } from './app-types.js'
export {
	escapeQuotes,
	capitalizeFirstLetter,
	uncapitalizeFirstLetter,
	sentenceCase,
	parseRegExp,
	calculateNextFreeName,
	generateRandomString,
} from './util/string-util.js'
export {
	UserTaskToken,
	UserTaskTokenDetails,
} from './process/user-task-token.js'
export { type AiType, AiTypes } from './app-types.js'
export { type LoginStage, LoginStages } from './app-types.js'
export type { BinaryData, BinaryFile, BinaryDataOrFile } from './app-types.js'
export type {
	ProcessTokenScope,
	ResourceAvailabilityOnClientType,
	ServiceIntegrationAvailabilityOnClientType,
} from './app-types.js'
export type { ClientLogLevel } from './app-types.js'
export {
	Code,
	Expression,
	TextOrCode,
	TextOrCode_assumeText,
	TextOrCode_assumeExpression,
	TextOrCode_assumeCode,
	TextOrExpression_assumeText,
	TextOrExpression_assumeExpression,
	ExpressionOrCode,
} from './data/text-or-code.js'

// Chat and messaging
export type { ChatMessage, ChatMessageAttachment } from './chat/chat-message.js'
export type * from '@lg/support-services'

export const UuidRegex =
	/^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
